import type { ScanCandidate } from "@hobbyshopify/backend-client";
import { describeCandidate } from "../core/primitives/candidates";
import { formatCents } from "../core/primitives/card-display";
import { sealedGuides } from "../core/primitives/sealed-edit";

/**
 * The products the lookup weighed, for the reviewer to pick between: one list,
 * a radio, the photo, the name and the set, and what confirming does with each
 * (add a copy to a card the shop has, or create one). A port of the iOS review
 * screen's "Identified as" rows.
 */
export function ScanCandidates({
  candidates,
  selectedProductId,
  picking,
  disabled,
  onPick,
  sealed = false,
}: {
  candidates: readonly ScanCandidate[];
  /** Sealed product: its market price, and its eBay listings as evidence. */
  sealed?: boolean;
  selectedProductId: string | null;
  /** The product a pick is in flight for. */
  picking: string | null;
  disabled: boolean;
  onPick: (productId: string) => void;
}) {
  return (
    <fieldset className="min-w-0 divide-y divide-line overflow-hidden rounded-row border border-line bg-surface">
      <legend className="sr-only">Identified as</legend>
      {candidates.map((candidate) => {
        const line = describeCandidate(candidate.product);
        const selected = line.productId !== null && line.productId === selectedProductId;
        const inFlight = line.productId !== null && line.productId === picking;
        const image = candidate.images[0];
        const market = sealed ? sealedGuides(candidate.product).marketCents : null;
        const off = disabled || line.productId === null || picking !== null;
        return (
          <label
            key={line.productId ?? `rank-${candidate.rank}`}
            className={`flex items-center gap-3 p-3 transition-colors ${
              selected ? "bg-accent/10" : off ? "" : "cursor-pointer hover:bg-surface-strong"
            } ${line.productId === null ? "opacity-60" : ""}`}
          >
            <input
              type="radio"
              name="scan-candidate"
              className="h-[18px] w-[18px] shrink-0 accent-accent"
              checked={selected}
              disabled={off}
              onChange={() => line.productId && onPick(line.productId)}
              aria-label={`Identify as ${line.title}${line.subtitle ? `, ${line.subtitle}` : ""}`}
            />
            <span className="h-[50px] w-9 shrink-0 overflow-hidden rounded bg-surface-strong">
              {image && <img src={image} alt="" className="h-full w-full object-cover" loading="lazy" />}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-semibold text-white">{line.title}</span>
              <span className="block truncate text-xs text-muted">{line.subtitle}</span>
              {sealed && candidate.listings.some((listing) => listing.url) && (
                <span className="mt-1.5 block">
                  <span className="block text-[11px] text-faint">Similar listings on eBay</span>
                  <span className="mt-1 flex gap-1.5">
                    {candidate.listings.slice(0, 4).map((listing) =>
                      listing.url ? (
                        <a
                          key={listing.url}
                          href={listing.url}
                          target="_blank"
                          rel="noreferrer"
                          title={listing.title ?? undefined}
                          className="h-10 w-10 overflow-hidden rounded border border-line"
                        >
                          <img
                            src={listing.image}
                            alt={listing.title ?? ""}
                            className="h-full w-full object-cover"
                            loading="lazy"
                          />
                        </a>
                      ) : null,
                    )}
                  </span>
                </span>
              )}
            </span>
            <span className="flex shrink-0 flex-col items-end gap-1">
              {candidate.matchKind === "PRODUCT" ? (
                <span className="badge badge-accent">Matches stock</span>
              ) : (
                <span className="badge badge-neutral">{sealed ? "New product" : "New card"}</span>
              )}
              {market != null && <span className="text-[11px] text-faint">Market {formatCents(market)}</span>}
              <span className="text-[11px] text-faint">
                {inFlight ? "Switching…" : candidate.confidence != null ? `${candidate.confidence}% match` : ""}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
